import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Upload, Loader2, Trash2, Eye, Image as ImageIcon, X, Sparkles, CheckCircle2 } from "lucide-react";
import api, { API_ROOT, formatApiError } from "@/lib/api";

/**
 * Upload / lista / visualização de anexos AGA — foco no Teste do Relógio.
 *
 * Props:
 *   consultationId — obrigatório
 *   patientId      — usado só para exibição
 *   kind           — default "clock_drawing"
 *   title          — cabeçalho do card
 *
 * O backend expõe:
 *   POST   /api/consultations/{id}/aga-attachments   (multipart)
 *   GET    /api/consultations/{id}/aga-attachments?kind=
 *   GET    /api/aga-attachments/{aid}/file           (imagem/PDF)
 *   DELETE /api/aga-attachments/{aid}
 */
export default function ClockDrawingAttachment({
    consultationId, patientId, kind = "clock_drawing",
    title = "Teste do Relógio — foto",
    onAcceptScore,   // (score:number, analysisResult:object) => void — chama quando médico aceita score da IA
}) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploading, setUploading] = useState(false);
    const [preview, setPreview] = useState(null);
    const [analyzing, setAnalyzing] = useState(null);   // attachment_id em análise
    const [analysisFor, setAnalysisFor] = useState(null); // attachment aberto no dialog de análise
    const inputRef = useRef(null);

    const load = async () => {
        if (!consultationId) return;
        setLoading(true);
        try {
            const { data } = await api.get(`/consultations/${consultationId}/aga-attachments`, { params: { kind } });
            setItems(data || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [consultationId, kind]);

    const upload = async (file) => {
        if (!file) return;
        if (file.size > 15 * 1024 * 1024) {
            toast.error("Arquivo excede 15 MB.");
            return;
        }
        const form = new FormData();
        form.append("file", file);
        form.append("kind", kind);
        setUploading(true);
        try {
            await api.post(`/consultations/${consultationId}/aga-attachments`, form, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Anexo enviado.");
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally {
            setUploading(false);
            if (inputRef.current) inputRef.current.value = "";
        }
    };

    const remove = async (aid) => {
        if (!window.confirm("Remover este anexo?")) return;
        try {
            await api.delete(`/aga-attachments/${aid}`);
            setItems((arr) => arr.filter((i) => i.id !== aid));
            toast.success("Anexo removido.");
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const analyze = async (att) => {
        setAnalyzing(att.id);
        try {
            const { data } = await api.post(`/aga-attachments/${att.id}/analyze-clock`);
            const updated = { ...att, ai_analysis: data };
            setItems((arr) => arr.map((i) => i.id === att.id ? updated : i));
            setAnalysisFor(updated);
            if (data.score === null || !data.quality_ok) {
                toast.warning("IA não conseguiu extrair uma pontuação confiável. Revise a foto.");
            } else {
                toast.success(`IA sugere score ${data.score}/10 — ${data.band}`);
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setAnalyzing(null); }
    };

    return (
        <div className="rounded-xl border-2 border-dashed border-[#A08A5C] p-4 bg-[#FBF7EF]/60" data-testid={`clock-attachment-${kind}`}>
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center shrink-0">
                        <Camera size={16} />
                    </div>
                    <div className="min-w-0">
                        <h4 className="font-display text-sm text-brand font-medium">{title}</h4>
                        <p className="text-[11px] text-muted-clinical">
                            Anexe uma foto do teste (formato imagem ou PDF, até 15 MB).
                        </p>
                    </div>
                </div>
                <div>
                    <input
                        ref={inputRef}
                        type="file"
                        accept="image/*,application/pdf"
                        onChange={(e) => upload(e.target.files?.[0])}
                        className="hidden"
                        data-testid={`clock-upload-input-${kind}`}
                    />
                    <button
                        onClick={() => inputRef.current?.click()}
                        disabled={uploading || !consultationId}
                        className="btn-primary text-xs"
                        data-testid={`clock-upload-btn-${kind}`}
                    >
                        {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Enviar foto
                    </button>
                </div>
            </div>

            {loading ? (
                <p className="text-xs text-muted-clinical">Carregando…</p>
            ) : items.length === 0 ? (
                <p className="text-xs text-muted-clinical text-center py-3">Nenhum anexo enviado.</p>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {items.map((att) => (
                        <div key={att.id} className="relative group rounded-lg overflow-hidden bg-white border border-[#EDE0C6]">
                            <button
                                type="button"
                                onClick={() => setPreview(att)}
                                data-testid={`clock-thumb-${att.id}`}
                                className="block w-full aspect-square hover:opacity-90 transition-opacity"
                            >
                                {att.content_type?.startsWith("image/") ? (
                                    <img
                                        src={`${API_ROOT}/aga-attachments/${att.id}/file`}
                                        alt="Teste do relógio"
                                        className="w-full h-full object-cover"
                                    />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-alt text-brand">
                                        <ImageIcon size={24} />
                                    </div>
                                )}
                            </button>
                            {/* AI score badge */}
                            {att.ai_analysis?.score !== undefined && att.ai_analysis?.score !== null && (
                                <button
                                    onClick={() => setAnalysisFor(att)}
                                    className="absolute top-1.5 right-1.5 bg-[#2E7D6B] text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-md hover:bg-[#1F5A4C] flex items-center gap-1"
                                    data-testid={`clock-ai-badge-${att.id}`}
                                >
                                    <Sparkles size={9} /> IA {att.ai_analysis.score}/10
                                </button>
                            )}
                            <div className="absolute inset-x-0 bottom-0 bg-black/60 backdrop-blur-sm text-white text-[10px] px-2 py-1 flex items-center justify-between">
                                <span className="truncate">{new Date(att.created_at).toLocaleDateString("pt-BR")}</span>
                                <div className="flex gap-1">
                                    {att.content_type?.startsWith("image/") && (
                                        <button
                                            onClick={() => analyze(att)}
                                            disabled={analyzing === att.id}
                                            className="hover:text-[#F5C518] disabled:opacity-50"
                                            title={att.ai_analysis ? "Reanalisar com IA" : "Analisar com IA"}
                                            data-testid={`clock-analyze-${att.id}`}
                                        >
                                            {analyzing === att.id
                                                ? <Loader2 size={12} className="animate-spin" />
                                                : <Sparkles size={12} />}
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setPreview(att)}
                                        className="hover:text-[#F5C518]"
                                        title="Visualizar"
                                        data-testid={`clock-view-${att.id}`}
                                    >
                                        <Eye size={12} />
                                    </button>
                                    <button
                                        onClick={() => remove(att.id)}
                                        className="hover:text-[#F5C518]"
                                        title="Remover"
                                        data-testid={`clock-remove-${att.id}`}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {preview && <PreviewModal att={preview} onClose={() => setPreview(null)} />}
            {analysisFor && (
                <AnalysisModal
                    att={analysisFor}
                    onClose={() => setAnalysisFor(null)}
                    onAcceptScore={(score) => {
                        if (onAcceptScore) onAcceptScore(score, analysisFor.ai_analysis);
                        toast.success(`Score ${score}/10 aplicado ao campo Teste do Relógio da AGA.`);
                        setAnalysisFor(null);
                    }}
                />
            )}
        </div>
    );
}

function PreviewModal({ att, onClose }) {
    const url = `${API_ROOT}/aga-attachments/${att.id}/file`;
    return (
        <div
            className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="clock-preview-modal"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden">
                <div className="p-3 border-b flex items-center justify-between bg-[#FBF7EF]">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-widest text-[#8A6A15] font-semibold">Anexo AGA</div>
                        <div className="text-sm text-brand truncate">{att.filename}</div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center">
                        <X size={14} />
                    </button>
                </div>
                <div className="flex-1 overflow-auto bg-[#111] flex items-center justify-center">
                    {att.content_type?.startsWith("image/") ? (
                        <img src={url} alt="preview" className="max-w-full max-h-[80vh] object-contain" />
                    ) : (
                        <iframe title="preview" src={url} className="w-full h-[80vh] bg-white" />
                    )}
                </div>
                {att.notes && <div className="p-3 bg-white text-xs text-muted-clinical border-t">{att.notes}</div>}
            </div>
        </div>
    );
}

function AnalysisModal({ att, onClose, onAcceptScore }) {
    const a = att.ai_analysis || {};
    const url = `${API_ROOT}/aga-attachments/${att.id}/file`;
    const [manualScore, setManualScore] = useState(a.score ?? "");
    const bandTone = (b) => {
        const s = (b || "").toLowerCase();
        if (s.includes("grave"))    return "bg-[#F9ECEC] text-[#9E2A2B] border-[#9E2A2B]";
        if (s.includes("moderad"))  return "bg-[#FBF4E5] text-[#8A6A15] border-[#D4A574]";
        if (s.includes("leve"))     return "bg-[#FBF7EF] text-[#8A6A15] border-[#D4A574]";
        if (s.includes("normal"))   return "bg-[#EDF3E8] text-[#556B2F] border-[#556B2F]";
        return "bg-alt text-brand border-[rgba(30,63,51,0.2)]";
    };
    return (
        <div
            className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
            data-testid="clock-analysis-modal"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[92vh] flex flex-col overflow-hidden border-2 border-[#2E7D6B]/40">
                <div className="p-3 border-b flex items-center justify-between bg-gradient-to-r from-[#EAF6F2] to-white">
                    <div className="flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-[#2E7D6B] text-white flex items-center justify-center">
                            <Sparkles size={16} />
                        </div>
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-[#1F5A4C] font-semibold">Análise IA · Sunderland</p>
                            <h3 className="font-display text-sm text-brand font-medium">Teste do Relógio — pontuação sugerida</h3>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-alt hover:bg-brand hover:text-white flex items-center justify-center">
                        <X size={14} />
                    </button>
                </div>

                <div className="grid md:grid-cols-2 gap-3 p-4 overflow-y-auto">
                    {/* Left — image */}
                    <div className="rounded-lg overflow-hidden border border-[#EDE0C6] bg-[#111] flex items-center justify-center min-h-[240px]">
                        <img src={url} alt="Desenho do relógio" className="max-w-full max-h-[60vh] object-contain" />
                    </div>
                    {/* Right — result + accept */}
                    <div className="space-y-3">
                        {a.score !== undefined && a.score !== null ? (
                            <div className={`rounded-xl border-2 p-4 ${bandTone(a.band)}`}>
                                <div className="flex items-baseline gap-3">
                                    <div className="font-display text-4xl font-bold">{a.score}<span className="text-lg opacity-70">/10</span></div>
                                    <div>
                                        <div className="text-sm font-semibold">{a.band}</div>
                                        {a.confidence !== null && a.confidence !== undefined && (
                                            <div className="text-[11px] opacity-80">confiança {Math.round(a.confidence * 100)}%</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="rounded-xl border-2 p-4 bg-[#FBF4E5] text-[#8A6A15] border-[#D4A574]">
                                <div className="text-sm font-semibold mb-1">IA não conseguiu extrair uma pontuação confiável</div>
                                <div className="text-xs">{a.observations || "Revise a foto e tente novamente."}</div>
                            </div>
                        )}

                        {a.observations && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-clinical font-semibold mb-1">Observações</div>
                                <p className="text-xs text-brand leading-relaxed">{a.observations}</p>
                            </div>
                        )}
                        {a.issues?.length > 0 && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-clinical font-semibold mb-1">Achados específicos</div>
                                <ul className="list-disc pl-4 text-xs text-brand space-y-0.5">
                                    {a.issues.map((it, i) => <li key={i}>{it}</li>)}
                                </ul>
                            </div>
                        )}
                        {a.recommendation && (
                            <div>
                                <div className="text-[10px] uppercase tracking-widest text-muted-clinical font-semibold mb-1">Sugestão clínica</div>
                                <p className="text-xs text-brand">{a.recommendation}</p>
                            </div>
                        )}

                        {/* Accept / adjust */}
                        <div className="rounded-lg border border-[#EDE0C6] p-3 bg-[#FBF7EF]">
                            <div className="text-[10px] uppercase tracking-widest text-[#8A6A15] font-semibold mb-2">
                                Confirmar pontuação no prontuário
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="number" min={0} max={10}
                                    value={manualScore}
                                    onChange={(e) => setManualScore(e.target.value)}
                                    className="input-strong w-20 text-center font-bold"
                                    data-testid="clock-final-score"
                                />
                                <span className="text-xs text-muted-clinical">/10</span>
                                <button
                                    onClick={() => {
                                        const v = parseInt(manualScore, 10);
                                        if (Number.isNaN(v) || v < 0 || v > 10) {
                                            return;
                                        }
                                        onAcceptScore(v);
                                    }}
                                    disabled={manualScore === "" || Number.isNaN(parseInt(manualScore, 10))}
                                    className="btn-primary text-xs ml-auto"
                                    data-testid="clock-accept-score"
                                >
                                    <CheckCircle2 size={12} /> Aplicar na AGA
                                </button>
                            </div>
                            <p className="text-[10px] text-muted-clinical mt-2">
                                A sugestão da IA é orientativa. Você pode ajustar o valor antes de aplicar.
                            </p>
                        </div>

                        <p className="text-[10px] text-muted-clinical">
                            {a.reference} · Modelo: {a.model}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

